import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMonetization, type Product, type PurchaseResult, type AdResult } from '@/arcade'
import { useProgress } from './ProgressProvider'
import { POWERUPS, computeVipTier } from '@/game/catalog'
import { getCharacter } from '@/game/characters'
import { getProp } from '@/game/props'
import type { TakeMeHomeSave } from '@/types/save'

interface ShopContextValue {
  products: Product[]
  loadingProducts: boolean
  productsError: string | null
  iapConfigured: boolean
  adsConfigured: boolean
  adsReady: boolean
  purchase: (productId: string) => Promise<PurchaseResult>
  showRewarded: () => Promise<AdResult>
  refreshProducts: () => Promise<void>
  playerLevel: number
  buyPowerup: (id: string) => boolean
  buyCharacter: (id: string) => boolean
  equipCharacter: (id: string) => boolean
  buyProp: (id: string) => boolean
  equipProp: (id: string) => boolean
  unequipProp: (id: string) => boolean
}

const ShopContext = createContext<ShopContextValue | null>(null)

function withSpend(save: TakeMeHomeSave, coins: number, gems: number): TakeMeHomeSave {
  const totalSpent = {
    coins: save.meta.totalSpent.coins + coins,
    gems: save.meta.totalSpent.gems + gems,
  }
  return {
    ...save,
    economy: {
      ...save.economy,
      coins: save.economy.coins - coins,
      gems: save.economy.gems - gems,
    },
    meta: { ...save.meta, totalSpent, vipTier: computeVipTier(totalSpent) },
  }
}

export function ShopProvider({ children }: { children: ReactNode }) {
  const mon = useMonetization()
  const { save, update } = useProgress()
  const [products, setProducts] = useState<Product[]>([])
  const [loadingProducts, setLoadingProducts] = useState(true)
  const [productsError, setProductsError] = useState<string | null>(null)

  const playerLevel = save.progress.completedLevels.length

  const refreshProducts = useCallback(async () => {
    setLoadingProducts(true)
    setProductsError(null)
    try {
      setProducts(await mon.getProducts())
    } catch {
      setProductsError('Could not load the store. Try again.')
    } finally {
      setLoadingProducts(false)
    }
  }, [mon])

  useEffect(() => {
    void refreshProducts()
  }, [refreshProducts])

  const applyGrant = useCallback(
    (grant: Record<string, number> | undefined) => {
      if (!grant) return
      update((s) => {
        const next = {
          ...s,
          economy: { ...s.economy },
          meta: { ...s.meta },
          cosmetics: { ...s.cosmetics },
          powerups: { ...s.powerups },
        }
        if (grant.coins) next.economy.coins += grant.coins
        if (grant.gems) next.economy.gems += grant.gems
        if (grant.removeAds) next.meta.removeAds = true
        for (const key of Object.keys(grant)) {
          if (key.startsWith('skin_')) {
            const skinId = key.replace('skin_', 'ember_')
            if (!next.cosmetics.ownedSkins.includes(skinId)) {
              next.cosmetics.ownedSkins = [...next.cosmetics.ownedSkins, skinId]
            }
          } else if (key.startsWith('character_')) {
            const charId = key.replace('character_', '')
            if (getCharacter(charId) && !next.cosmetics.ownedCharacters.includes(charId)) {
              next.cosmetics.ownedCharacters = [...next.cosmetics.ownedCharacters, charId]
            }
          } else if (key.startsWith('prop_')) {
            const propId = key.replace('prop_', '').replace(/_/g, '-')
            if (getProp(propId) && !next.cosmetics.ownedProps.includes(propId)) {
              next.cosmetics.ownedProps = [...next.cosmetics.ownedProps, propId]
            }
          } else if (key.startsWith('perm_')) {
            next.powerups[key] = 1
          }
        }
        return next
      })
    },
    [update],
  )

  const purchase = useCallback(
    async (productId: string): Promise<PurchaseResult> => {
      const result = await mon.purchase(productId)
      if (result.status === 'purchased') {
        const product = products.find((p) => p.id === productId)
        applyGrant(product?.grant)
      }
      return result
    },
    [mon, products, applyGrant],
  )

  const showRewarded = useCallback(() => mon.showRewarded(), [mon])

  const buyPowerup = useCallback(
    (id: string): boolean => {
      const def = POWERUPS.find((p) => p.id === id)
      if (!def) return false
      if (playerLevel < def.unlockLevel) return false
      const owned = save.powerups[id] ?? 0
      if (owned >= def.maxQuantity) return false
      const coins = def.currency === 'coins' ? def.cost : 0
      const gems = def.currency === 'gems' ? def.cost : 0
      if (save.economy.coins < coins || save.economy.gems < gems) return false
      update((s) => {
        const spent = withSpend(s, coins, gems)
        return { ...spent, powerups: { ...spent.powerups, [id]: (spent.powerups[id] ?? 0) + 1 } }
      })
      return true
    },
    [playerLevel, save.economy.coins, save.economy.gems, save.powerups, update],
  )

  const buyCharacter = useCallback(
    (id: string): boolean => {
      const def = getCharacter(id)
      if (!def || def.iapOnly) return false
      if (save.cosmetics.ownedCharacters.includes(id)) return false
      const levelUnlocked = def.unlockLevel != null && playerLevel >= def.unlockLevel
      const coins = def.price?.coins ?? 0
      const gems = def.price?.gems ?? 0
      if (levelUnlocked || (!def.price && def.unlockLevel == null)) {
        update((s) => ({
          ...s,
          cosmetics: { ...s.cosmetics, ownedCharacters: [...s.cosmetics.ownedCharacters, id] },
        }))
        return true
      }
      if (!def.price) return false
      if (save.economy.coins < coins || save.economy.gems < gems) return false
      update((s) => {
        const spent = withSpend(s, coins, gems)
        return {
          ...spent,
          cosmetics: { ...spent.cosmetics, ownedCharacters: [...spent.cosmetics.ownedCharacters, id] },
        }
      })
      return true
    },
    [playerLevel, save.cosmetics.ownedCharacters, save.economy.coins, save.economy.gems, update],
  )

  const equipCharacter = useCallback(
    (id: string): boolean => {
      if (!save.cosmetics.ownedCharacters.includes(id)) return false
      update((s) => ({ ...s, cosmetics: { ...s.cosmetics, equippedCharacter: id } }))
      return true
    },
    [save.cosmetics.ownedCharacters, update],
  )

  const buyProp = useCallback(
    (id: string): boolean => {
      const def = getProp(id)
      if (!def) return false
      if (save.cosmetics.ownedProps.includes(id)) return false
      const coins = def.price.coins ?? 0
      const gems = def.price.gems ?? 0
      if (save.economy.coins < coins || save.economy.gems < gems) return false
      update((s) => {
        const spent = withSpend(s, coins, gems)
        return { ...spent, cosmetics: { ...spent.cosmetics, ownedProps: [...spent.cosmetics.ownedProps, id] } }
      })
      return true
    },
    [save.cosmetics.ownedProps, save.economy.coins, save.economy.gems, update],
  )

  const equipProp = useCallback(
    (id: string): boolean => {
      const def = getProp(id)
      if (!def) return false
      if (!save.cosmetics.ownedProps.includes(id)) return false
      update((s) => {
        const kept = s.cosmetics.equippedProps.filter((pid) => {
          const p = getProp(pid)
          return p != null && p.slot !== def.slot
        })
        return { ...s, cosmetics: { ...s.cosmetics, equippedProps: [...kept, id] } }
      })
      return true
    },
    [save.cosmetics.ownedProps, update],
  )

  const unequipProp = useCallback(
    (id: string): boolean => {
      if (!save.cosmetics.equippedProps.includes(id)) return false
      update((s) => ({
        ...s,
        cosmetics: { ...s.cosmetics, equippedProps: s.cosmetics.equippedProps.filter((pid) => pid !== id) },
      }))
      return true
    },
    [save.cosmetics.equippedProps, update],
  )

  const ctx = useMemo<ShopContextValue>(
    () => ({
      products,
      loadingProducts,
      productsError,
      iapConfigured: mon.configured.iap,
      adsConfigured: mon.configured.ads,
      adsReady: mon.adsReady(),
      purchase,
      showRewarded,
      refreshProducts,
      playerLevel,
      buyPowerup,
      buyCharacter,
      equipCharacter,
      buyProp,
      equipProp,
      unequipProp,
    }),
    [products, loadingProducts, productsError, mon, purchase, showRewarded, refreshProducts, playerLevel, buyPowerup, buyCharacter, equipCharacter, buyProp, equipProp, unequipProp],
  )

  return <ShopContext.Provider value={ctx}>{children}</ShopContext.Provider>
}

export function useShop(): ShopContextValue {
  const ctx = useContext(ShopContext)
  if (!ctx) throw new Error('useShop must be used within <ShopProvider>')
  return ctx
}
